/**
 * Public, indexable route registry — the single source of truth for both
 * `sitemap.ts` and the per-page `alternates` (canonical) metadata.
 *
 * Keeping this in one place means a new public page is added to the sitemap in
 * exactly one edit. The app is English-only for public/indexable content
 * (legal pages included), so there are no hreflang language pairs.
 */

/**
 * Bilingual marketing pages: English at the bare path, Slovenian under a `/sl`
 * prefix with a TRANSLATED slug (e.g. /pricing ↔ /sl/cenik). EN is the default
 * (no prefix). These get reciprocal hreflang on both URLs and both appear in the
 * sitemap. Add a pair here once BOTH route files exist (the SL page lives under
 * src/app/sl/<slug>/page.tsx and is forced to Slovenian via middleware x-lang).
 */
export const BILINGUAL_PAGES: readonly {
  en: string;
  sl: string;
  changeFrequency: "daily" | "weekly" | "monthly" | "yearly";
  priority: number;
}[] = [
  { en: "/", sl: "/sl", changeFrequency: "weekly", priority: 1.0 },
  { en: "/pricing", sl: "/sl/cenik", changeFrequency: "monthly", priority: 0.9 },
  { en: "/faq", sl: "/sl/pogosta-vprasanja", changeFrequency: "monthly", priority: 0.8 },
  { en: "/blog", sl: "/sl/blog", changeFrequency: "weekly", priority: 0.7 },
  { en: "/about", sl: "/sl/o-nas", changeFrequency: "monthly", priority: 0.5 },
  { en: "/product/ai-assistant", sl: "/sl/produkt/ai-pomocnik", changeFrequency: "monthly", priority: 0.7 },
  { en: "/product/daily-rhythm", sl: "/sl/produkt/dnevni-ritem", changeFrequency: "monthly", priority: 0.7 },
  { en: "/product/integrations", sl: "/sl/produkt/integracije", changeFrequency: "monthly", priority: 0.7 },
  { en: "/product/goals", sl: "/sl/produkt/cilji", changeFrequency: "monthly", priority: 0.7 },
  { en: "/product/journal", sl: "/sl/produkt/dnevnik", changeFrequency: "monthly", priority: 0.7 },
];

/**
 * Client-safe: the URL for the same page in the other locale. Used by the
 * header language switcher to navigate EN↔SL instead of toggling a cookie.
 * Falls back to the locale home when the current page has no translation.
 */
export function localePathFor(pathname: string, locale: "en" | "sl"): string {
  const path = pathname.replace(/\/+$/, "") || "/";
  const pair = BILINGUAL_PAGES.find((p) => p.en === path || p.sl === path);
  if (pair) return locale === "sl" ? pair.sl : pair.en;
  return locale === "sl" ? "/sl" : "/";
}

/**
 * Build the `alternates` (canonical + hreflang languages) for a bilingual page,
 * given its EN path and which locale is rendering. Returns a plain
 * `{ canonical }` for pages that aren't bilingual yet.
 */
export function altsFor(
  enPath: string,
  locale: "en" | "sl",
): { canonical: string; languages?: Record<string, string> } {
  const pair = BILINGUAL_PAGES.find((p) => p.en === enPath);
  if (!pair) return { canonical: enPath };
  return {
    canonical: locale === "sl" ? pair.sl : pair.en,
    languages: { en: pair.en, sl: pair.sl, "x-default": pair.en },
  };
}

/** Single-locale (English) public pages. */
export const SINGLE_PAGES: readonly {
  path: string;
  changeFrequency: "daily" | "weekly" | "monthly" | "yearly";
  priority: number;
}[] = [
  { path: "/", changeFrequency: "weekly", priority: 1.0 },
  { path: "/pricing", changeFrequency: "monthly", priority: 0.9 },
  { path: "/faq", changeFrequency: "monthly", priority: 0.8 },
  { path: "/blog", changeFrequency: "weekly", priority: 0.7 },
  { path: "/about", changeFrequency: "monthly", priority: 0.5 },
  { path: "/compare", changeFrequency: "monthly", priority: 0.5 },
  { path: "/compare/journail-vs-motion", changeFrequency: "monthly", priority: 0.6 },
  { path: "/compare/journail-vs-sunsama", changeFrequency: "monthly", priority: 0.6 },
  { path: "/compare/journail-vs-akiflow", changeFrequency: "monthly", priority: 0.6 },
  { path: "/compare/notion-alternative", changeFrequency: "monthly", priority: 0.6 },
  { path: "/compare/todoist-alternative", changeFrequency: "monthly", priority: 0.6 },
  { path: "/contact", changeFrequency: "yearly", priority: 0.4 },
  { path: "/ai", changeFrequency: "yearly", priority: 0.3 },
  { path: "/gdpr", changeFrequency: "yearly", priority: 0.3 },
  { path: "/guides", changeFrequency: "monthly", priority: 0.5 },
  { path: "/guides/tutorials", changeFrequency: "monthly", priority: 0.6 },
  { path: "/guides/our-trail", changeFrequency: "weekly", priority: 0.5 },
  { path: "/product/daily-rhythm", changeFrequency: "monthly", priority: 0.7 },
  { path: "/product/ai-assistant", changeFrequency: "monthly", priority: 0.7 },
  { path: "/product/integrations", changeFrequency: "monthly", priority: 0.7 },
  { path: "/product/goals", changeFrequency: "monthly", priority: 0.7 },
  { path: "/product/journal", changeFrequency: "monthly", priority: 0.7 },
  { path: "/privacy", changeFrequency: "yearly", priority: 0.3 },
  { path: "/terms", changeFrequency: "yearly", priority: 0.3 },
  { path: "/cookies", changeFrequency: "yearly", priority: 0.3 },
];
